import { useState } from "react";
import { Table } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "components/ui/tooltip";
import { TableDensity } from "./useTableViewState";

type DataTableToolbarProps<T> = {
  table: Table<T>;
  enableGlobalFilter?: boolean;
  enableColumnVisibility?: boolean;
  enableDensityToggle?: boolean;
  density?: TableDensity;
  onDensityChange?: (density: TableDensity) => void;
  /** Resets column visibility/order/sizing/pinning, sorting, filters, and density back to defaults. */
  onResetDefaults?: () => void;
};

export function DataTableToolbar<T>({
  table,
  enableGlobalFilter,
  enableColumnVisibility,
  enableDensityToggle,
  density,
  onDensityChange,
  onResetDefaults,
}: DataTableToolbarProps<T>) {
  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);

  if (!enableGlobalFilter && !enableColumnVisibility && !enableDensityToggle) {
    return null;
  }

  return (
    <div className="mb-2 flex flex-wrap items-center gap-2">
      {enableGlobalFilter ? (
        <Input
          aria-label="Quick filter"
          className="h-8 w-56"
          placeholder="Quick filter (this page)…"
          value={(table.getState().globalFilter as string) ?? ""}
          onChange={(e) => table.setGlobalFilter(e.target.value)}
        />
      ) : null}

      {enableDensityToggle && density && onDensityChange ? (
        <div aria-label="Row density" className="inline-flex rounded-md shadow-xs" role="group">
          <Button
            aria-label="Comfortable row spacing"
            aria-pressed={density === "comfortable"}
            className="rounded-r-none"
            onClick={() => onDensityChange("comfortable")}
            size="sm"
            type="button"
            variant={density === "comfortable" ? "secondary" : "outline"}
          >
            Comfortable
          </Button>
          <Button
            aria-label="Compact row spacing"
            aria-pressed={density === "compact"}
            className="-ml-px rounded-l-none"
            onClick={() => onDensityChange("compact")}
            size="sm"
            type="button"
            variant={density === "compact" ? "secondary" : "outline"}
          >
            Compact
          </Button>
        </div>
      ) : null}

      {enableColumnVisibility ? (
        <div className="ml-auto">
          <DropdownMenu
            onOpenChange={setColumnsMenuOpen}
            open={columnsMenuOpen}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    aria-label="Columns"
                    size="icon-sm"
                    type="button"
                    variant="ghost"
                  >
                    <FontAwesomeIcon aria-hidden="true" icon="table-columns" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>Columns</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end">
            {table
              .getAllLeafColumns()
              .filter((column) => column.getCanHide())
              .map((column) => (
                <DropdownMenuCheckboxItem
                  key={column.id}
                  checked={column.getIsVisible()}
                  onCheckedChange={(checked) =>
                    column.toggleVisibility(checked)
                  }
                  onSelect={(event) => event.preventDefault()}
                >
                  {typeof column.columnDef.header === "string"
                    ? column.columnDef.header
                    : column.id}
                </DropdownMenuCheckboxItem>
              ))}
            {onResetDefaults ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => {
                    onResetDefaults();
                    setColumnsMenuOpen(false);
                  }}
                >
                  <FontAwesomeIcon aria-hidden="true" icon="rotate" />
                  Restore default
                </DropdownMenuItem>
              </>
            ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : null}
    </div>
  );
}
